import _ from "lodash";
import config from "config";
import { parseConfigurationValue } from "Utils/Common";
import {
  MODULE_CONFIGURATION_MAP,
  CONFIG_STAFF_PERM_MAP,
  STAFF_PERMISSIONS_CONFIGURATIONS,
  MODULES,
} from "./staffPermissionMaps";
import { getConfigurationValueFromCache } from "Utils/ConfigurationUtils/GraphqlHelpers";

const isStagingOrDevelopment = _.includes(
  ["staging", "development"],
  config.env
);

const CAN_SHOW_STATES = ["ENABLED", "LOCKED"];

const LOCK_STATES = ["LOCKED"];

const INITIAL_CONFIG_VALUE = {
  ORGANIZATION: {},
  PARENT_CURRICULUM: {},
  CURRICULUM: {},
  COURSE: {},
  COMMUNITY: {},
  PARENT_COURSE: {},
  LEARNING_COURSE: {},
  PARENT_LEARNING_COURSE: {},
  HOME_PAGE: {},
};

class ACLStore {
  constructor() {
    if (!ACLStore.instance) {
      this.configurationMap = _.cloneDeep(INITIAL_CONFIG_VALUE);
      ACLStore.instance = this;
    }
    return ACLStore.instance;
  }
  #countOfSubscribersByContext = {};

  isStaffPermissionEnabled = module => {
    const isEnableByFlag = _.includes(
      CAN_SHOW_STATES,
      this.getPermissionStatus(STAFF_PERMISSIONS_CONFIGURATIONS.FEATURE_FLAG)
    );
    let isEnabledByModuleAvailibility = true;
    const { perm } = MODULES[module] || {};
    if (perm) {
      isEnabledByModuleAvailibility = this.can(perm);
    }
    return isEnableByFlag && isEnabledByModuleAvailibility;
  };

  hasCurriculumProgrammeAdminAccess = curriculumId => {
    if (!this.isStaffPermissionEnabled()) return true;
    const configValue = this.getConfigObj(
      STAFF_PERMISSIONS_CONFIGURATIONS.PROGRAMME_ADMINS
    );

    if (_.isUndefined(configValue)) {
      // this configuration is not present for the current user, hence it was invalid for current user, hence the user is not of role admin
      return false;
    }
    const configParsedValue = parseConfigurationValue(configValue);

    if (_.includes(configParsedValue, "ALL")) return true;

    return _.includes(configParsedValue, parseInt(curriculumId));
  };

  getUserModuleAccess = moduleName => {
    const configName = MODULE_CONFIGURATION_MAP[moduleName];

    if (!this.isStaffPermissionEnabled()) return;

    if (!configName) {
      console.warn("Invalid moduleName:- ", moduleName);
      return;
    }

    const moduleInfo = MODULES[moduleName];
    const availablityPermission = _.get(moduleInfo, "perm");
    if (availablityPermission && !this.can(availablityPermission)) {
      return;
    }
    return parseConfigurationValue(this.getConfigObj(configName));
  };

  isPermAllowedByStaffPermission = perm => {
    if (!CONFIG_STAFF_PERM_MAP[perm]) return;

    const {
      staffPermissionConfiguration,
      allowedAccess,
    } = CONFIG_STAFF_PERM_MAP[perm];

    const configValue = parseConfigurationValue(
      this.getConfigObj(staffPermissionConfiguration)
    );

    return _.includes(allowedAccess, configValue);
  };

  getConfigObj = (configName, readFromUserAndOrgLevel) => {
    const {
      ORGANIZATION,
      PARENT_CURRICULUM,
      CURRICULUM,
      COURSE,
      COMMUNITY,
      PARENT_COURSE,
      LEARNING_COURSE,
      PARENT_LEARNING_COURSE,
    } = this.configurationMap;

    const isCommunityConfiguration = _.includes(configName, "CommunityPortal");
    const isIdentityConfiguration = _.includes(configName, "IdentityAccess");

    let configObj = {};

    if (isCommunityConfiguration) {
      configObj = _.get(COMMUNITY, configName, {});
    } else if (isIdentityConfiguration || readFromUserAndOrgLevel) {
      configObj = _.get(ORGANIZATION, configName, {});
    } else {
      configObj =
        _.get(PARENT_COURSE, configName) ??
        _.get(COURSE, configName) ??
        _.get(PARENT_LEARNING_COURSE, configName) ??
        _.get(LEARNING_COURSE, configName) ??
        _.get(PARENT_CURRICULUM, configName) ??
        _.get(CURRICULUM, configName) ??
        _.get(ORGANIZATION, configName) ??
        {};
    }

    const isLocked = _.get(configObj, "isLocked");
    const isEnabled = parseConfigurationValue(configObj);

    configObj = {
      ...configObj,
      state: isLocked ? "LOCKED" : isEnabled ? "ENABLED" : "DISABLED",
    };

    return configObj;
  };

  getPermissionState = perm => {
    const isCan = this.can(perm);
    const isLocked = this.isLocked(perm);
    if (isLocked) return "LOCKED";

    return isCan ? "ENABLED" : "DISABLED";
  };

  getPermissionStatus = (perm, readFromUserAndOrgLevel) => {
    const configObj = this.getConfigObj(perm, readFromUserAndOrgLevel);

    if (
      isStagingOrDevelopment &&
      _.get(configObj, "aclControlParameterType") === "PREFERENCE"
    ) {
      console.warn(
        `Reading preferences from the ACL Store is deprecated and will be removed in future. Use the getSettingValue function from Utils/ConfigurationUtils.js for preference access.\nAffected configuration: ${_.get(
          configObj,
          "configuration"
        )}`
      );
    }
    const configValue = _.get(configObj, "state");
    return configValue;
  };

  validateCanShowStates = (perm, readFromUserAndOrgLevel) => {
    const state = this.getPermissionStatus(perm, readFromUserAndOrgLevel);

    if (this.isStaffPermissionEnabled()) {
      const isAccessAllowed = this.isPermAllowedByStaffPermission(perm);
      if (!_.isUndefined(isAccessAllowed)) {
        return isAccessAllowed && _.includes(CAN_SHOW_STATES, state);
      }
    }

    return _.includes(CAN_SHOW_STATES, state);
  };

  /* Default value for permissionsToCheck is 'one' which validates if 1 permission is valid, 
  pass 'all' to permissionsToCheck to validate all permissions */
  can = (
    perms,
    permissionsToCheck = "one",
    readFromUserAndOrgLevel = false
  ) => {
    if (_.isArray(perms)) {
      return permissionsToCheck == "one" || !permissionsToCheck
        ? _.some(perms, perm =>
            this.validateCanShowStates(perm, readFromUserAndOrgLevel)
          )
        : _.every(perms, perm =>
            this.validateCanShowStates(perm, readFromUserAndOrgLevel)
          );
    }
    return this.validateCanShowStates(perms, readFromUserAndOrgLevel);
  };

  canWithContext = (perms, context, permissionsToCheck = "one") => {
    if (_.isArray(perms)) {
      return permissionsToCheck == "one" || !permissionsToCheck
        ? _.some(perms, perm =>
            getConfigurationValueFromCache({ context, configurationName: perm })
          )
        : _.every(perms, perm =>
            getConfigurationValueFromCache({ context, configurationName: perm })
          );
    }

    return getConfigurationValueFromCache({
      context,
      configurationName: perms,
    });
  };

  isLocked = perms => {
    const validateLockState = perm =>
      _.includes(LOCK_STATES, this.getPermissionStatus(perm));

    return _.isArray(perms)
      ? _.every(perms, validateLockState)
      : validateLockState(perms);
  };

  getPermissionsArray = () => {
    const permissionObj = _.get(this.configurationMap, "ORGANIZATION", {});

    const permissionKeys = _.keys(permissionObj);

    return _.filter(permissionKeys, this.validateCanShowStates);
  };

  clearAclStore = () => {
    Object.assign(this.configurationMap, _.cloneDeep(INITIAL_CONFIG_VALUE));
  };

  clearGroup = groupId => {
    this.configurationMap[groupId] = {};
  };

  updateConfig = (key, configList) => {
    const map = _.keyBy(configList, "configuration");
    Object.assign(this.configurationMap[key], map);
  };

  subscribeToContext = contextId => {
    this.#countOfSubscribersByContext[contextId] =
      (this.#countOfSubscribersByContext[contextId] ?? 0) + 1;

    return () => {
      this.#countOfSubscribersByContext[contextId] -= 1;
      if (this.#countOfSubscribersByContext[contextId] === 0) {
        this.clearGroup(contextId);
      }
    };
  };
}

const instance = new ACLStore();
Object.freeze(instance);

export default instance;
